/**
 * Which control buttons a run offers. Driven by the run's `availableActions` —
 * the server's answer to "can THIS run be paused/cancelled/… right now?" — not
 * the static `capabilities` matrix (which only says the run *kind* ever could).
 * The list already applies the lifecycle preconditions; the endpoint's own
 * guards still catch races, so always surface the action's error rather than
 * assuming a listed action will succeed.
 */
import type { DeliveryRunDetail } from '../types/delivery-runs.types';

export interface DeliveryRunActions {
  canPause: boolean;
  canResume: boolean;
  canCancel: boolean;
  canRetryFailed: boolean;
  /** `true` when at least one action is available (the rail hides otherwise). */
  any: boolean;
}

/** Derive which control actions are offered for a run from `availableActions`. */
export function deriveDeliveryActions(run: DeliveryRunDetail): DeliveryRunActions {
  const actions = run.availableActions;
  const canPause = actions.includes('PAUSE');
  const canResume = actions.includes('RESUME');
  const canCancel = actions.includes('CANCEL');
  const canRetryFailed = actions.includes('RETRY_FAILED');

  return {
    canPause,
    canResume,
    canCancel,
    canRetryFailed,
    any: canPause || canResume || canCancel || canRetryFailed,
  };
}

/**
 * Whether "Retry delivery" (re-trigger the whole delivery as a NEW run) is offered.
 * Not an `availableActions` entry: it hits POST /deliveries/{connectionId}/deliver,
 * so it needs a finished run (a trigger while one is active 409/422s on the
 * per-connection guard) on a known connection. Shared by the control rail and the
 * products-unavailable card so the two never disagree.
 */
export function canRetryDelivery(run: Pick<DeliveryRunDetail, 'isTerminal' | 'connectionId'>) {
  return run.isTerminal && Boolean(run.connectionId);
}
